import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, StackDivider, VStack } from '@chakra-ui/react';
import { SETTLEMENT_TIMELINE_MANAGER_STRINGS, TAB_STATES } from '@views/SettlementTimelineManager/constant';
import useAuthRole from '@hooks/useAuthRole';
import { LoadingState, NoDataState } from '@components/StateComponents';
import { ConfirmUpdateModal, AddRangeModal } from './Modals';
import {
  HeaderSection,
  VersionSelectorSection,
  GlobalConfigurationSection,
  CountryConfigurationSection
} from './Sections';
import {
  useTimelineData,
  useFormDataUpdate,
  useEditMode,
  useAddRange
} from './hooks';
import { updateFulfillmentTimelineV2TimeUnit } from '@store/actions/settlementTimelineManagerActions';
import { TIME_UNITS } from '@constants/v2/common';


// Main Component
const NewFulfillmentTimelineDashboard = () => {
  const dispatch = useDispatch();
  const { hasAnyAuthority } = useAuthRole();
  const [selectedVersion, setSelectedVersion] = useState('1');

  // Custom hooks
  const {
    formData,
    setFormData,
    selectedCountry,
    setSelectedCountry,
    countries,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    isLoading,
    fetchData,
    timeUnit
  } = useTimelineData(selectedVersion);

  const {
    handleTopLevelChange,
    handleCountryLevelChange,
    handleBeneficiaryLevelChange,
    handleRangeChange
  } = useFormDataUpdate(setFormData, setHasUnsavedChanges);

  const {
    isEditMode,
    isModalLoading,
    isConfirmUpdateModalOpen,
    onConfirmUpdateModalClose,
    handleEditMode,
    handleCancelEdit,
    handleSave,
    handleConfirmSave,
    handleLoadVersion
  } = useEditMode(formData, fetchData, setFormData, setHasUnsavedChanges, hasUnsavedChanges);

  const {
    isAddRangeModalOpen,
    onAddRangeModalClose,
    handleAddRange,
    handleAddRangeSubmit
  } = useAddRange(setFormData, setHasUnsavedChanges);

  // Handle time unit change - just update Redux state
  const handleTimeUnitChange = useCallback((newTimeUnit) => {
    dispatch(updateFulfillmentTimelineV2TimeUnit({ timeUnit: newTimeUnit }));
  }, [dispatch]);

  // Helper function to convert minutes to display value based on current time unit
  const getDisplayValue = useCallback((valueInMinutes) => {
    if (valueInMinutes === null || valueInMinutes === undefined || valueInMinutes === '') return '';

    // Ensure we're working with a number
    const minutes = parseFloat(valueInMinutes);
    if (isNaN(minutes)) return '';

    if (timeUnit === TIME_UNITS.HOURS) {
      // Convert minutes to hours - no rounding, exact conversion
      return minutes / 60;
    }
    // Return exact value for minutes
    return minutes;
  }, [timeUnit]);

  // Helper function to convert user input back to minutes for storage
  const convertInputToMinutes = useCallback((value) => {
    if (value === null || value === undefined || value === '') return null;

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;

    if (timeUnit === TIME_UNITS.HOURS) {
      // Convert hours to minutes - exact conversion, no rounding
      return numValue * 60;
    }
    // Already in minutes - return exact value
    return numValue;
  }, [timeUnit]);

  if (isLoading && !formData) {
    return <LoadingState />;
  }

  if (!formData && !isLoading) {
    return (
      <NoDataState
        message={SETTLEMENT_TIMELINE_MANAGER_STRINGS.STRING_CONSTANTS[TAB_STATES.NEW_FULFILLMENT_TIMELINES_V2].NO_DATA_FOUND}
      />
    );
  }

  return (
    <Box position="relative" overflowX="hidden" overflowY="auto" flex="1">
      {/* Modals */}
      <ConfirmUpdateModal
        isOpen={isConfirmUpdateModalOpen}
        onClose={onConfirmUpdateModalClose}
        isLoading={isModalLoading}
        onConfirm={handleConfirmSave}
        message={SETTLEMENT_TIMELINE_MANAGER_STRINGS.STRING_CONSTANTS[TAB_STATES.NEW_FULFILLMENT_TIMELINES_V2].CONFIRM_UPDATE_MESSAGE}
      />

      <AddRangeModal
        isOpen={isAddRangeModalOpen}
        onClose={onAddRangeModalClose}
        onSubmit={handleAddRangeSubmit}
        isLoading={isModalLoading}
        timeUnit={timeUnit}
        convertInputToMinutes={convertInputToMinutes}
      />

      <VStack alignItems="stretch" gap={6} px={6} py={4} divider={<StackDivider borderColor="whiteAlpha.300" />}>
        {/* Header */}
        <HeaderSection
          hasUnsavedChanges={hasUnsavedChanges}
          hasAnyAuthority={hasAnyAuthority}
          isEditMode={isEditMode}
          handleEditMode={handleEditMode}
          handleCancelEdit={handleCancelEdit}
          handleSave={handleSave}
          timeUnit={timeUnit}
          handleTimeUnitChange={handleTimeUnitChange}
        />

        {/* Version Selector */}
        <VersionSelectorSection
          selectedVersion={selectedVersion}
          setSelectedVersion={setSelectedVersion}
          isEditMode={isEditMode}
          handleLoadVersion={handleLoadVersion}
        />

        {/* Main Content Grid */}
        <Grid templateColumns={{ base: '1fr', xl: '1fr 380px' }} gap={6}>
          {/* Country-Specific Configuration */}
          <CountryConfigurationSection
            countries={countries}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            formData={formData}
            isEditMode={isEditMode}
            handleCountryLevelChange={handleCountryLevelChange}
            handleBeneficiaryLevelChange={handleBeneficiaryLevelChange}
            handleRangeChange={handleRangeChange}
            handleAddRange={handleAddRange}
            timeUnit={timeUnit}
            getDisplayValue={getDisplayValue}
            convertInputToMinutes={convertInputToMinutes}
          />

          {/* Global Configuration */}
          <GlobalConfigurationSection
            formData={formData}
            isEditMode={isEditMode}
            handleTopLevelChange={handleTopLevelChange}
            timeUnit={timeUnit}
            getDisplayValue={getDisplayValue}
            convertInputToMinutes={convertInputToMinutes}
          />
        </Grid>
      </VStack>
    </Box>
  );
};

export default NewFulfillmentTimelineDashboard;